import React from 'react';
import {Form, FormProps, FormRenderProps} from 'react-final-form';

import {useTranslations} from 'src/components/TranslationsContext';
import * as tw from 'src/lib/tailwindClasses';

type Props = {
  name: string;
  isLoading: boolean;
  description?: string | JSX.Element;
  hasPermissions?: boolean;
  customFooter?: (props: {disabled: boolean}) => React.ReactNode;
} & FormProps;

export default function FormSection({
  name,
  isLoading,
  description,
  hasPermissions = true,
  customFooter,
  ...formProps
}: Props) {
  const {t} = useTranslations();
  return (
    <div>
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{name}</h3>
            <div className="mt-1 text-sm text-gray-600">{description}</div>
          </div>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          <Form
            {...formProps}
            onSubmit={formProps.onSubmit}
            render={(renderProps: FormRenderProps) => {
              const actionsDisabled = !hasPermissions || renderProps.pristine || isLoading;
              return (
                <form onSubmit={renderProps.handleSubmit}>
                  <div className="shadow sm:rounded-md sm:overflow-hidden">
                    <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                      {formProps.render!(renderProps)}
                    </div>
                    <div className="flex items-center justify-end px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-2">
                      {!hasPermissions && (
                        <div className="text-gray-400 mr-2">{t('contactAdminToEdit')}</div>
                      )}
                      {customFooter ? (
                        customFooter({disabled: actionsDisabled})
                      ) : (
                        <>
                          <button
                            className={tw.buttonSecondary}
                            disabled={actionsDisabled}
                            onClick={renderProps.form.reset}
                          >
                            {t('cancelChanges')}
                          </button>
                          <button
                            type="submit"
                            className={tw.buttonPrimary}
                            disabled={actionsDisabled}
                          >
                            {t('save')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </form>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function FormSectionSeparator() {
  return (
    <div className="hidden sm:block" aria-hidden="true">
      <div className="py-8">
        <div className="border-t border-gray-200" />
      </div>
    </div>
  );
}
